/**
 * CookieConsent — GDPR/ePrivacy-aware consent banner.
 * =====================================================
 *
 * Design choices:
 *   • Minimal friction — a single "Got it" button + a "Reject" button.
 *     No dark patterns, no "Manage my preferences" maze.
 *   • Only shows if we're actually setting non-essential cookies
 *     (right now = GA4). If GA4 measurement ID isn't configured we
 *     render nothing — no guilt-banner on a tracking-free site.
 *   • Choice is stored in localStorage with a 180-day TTL so we don't
 *     nag return visitors.
 *   • Reject blocks GA4 from firing (Analytics.js checks the same flag).
 *     Accept is a no-op — GA4 was already loaded on page view; we just
 *     stop suppressing page-view events going forward.
 */
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'tp_cookie_consent';
const TTL_DAYS = 180;

// Kiosk routes (e.g. /widget, /console) suppress the consent banner — these
// tablets are dedicated wall mounts and consent was given at signup. The
// banner would just block the screen forever on a glance-only display.
const KIOSK_PATHS = ['/console', '/widget'];

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';

// Helper other modules (Analytics.js) can call to respect the user's choice.
export const getConsentStatus = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;  // never answered
    const { value, expires } = JSON.parse(raw);
    if (Date.now() > expires) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
};

const writeConsent = (value) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ value, expires: Date.now() + TTL_DAYS * 24 * 60 * 60 * 1000 }),
    );
  } catch {
    /* ignore */
  }
};

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Suppress on kiosk routes — wall-mounted tablets don't need a consent
    // popover blocking the dashboard.
    if (KIOSK_PATHS.includes(location.pathname)) return;
    // Only show if (a) we actually track and (b) user hasn't already answered
    const tracksUsers = !!process.env.REACT_APP_GA4_MEASUREMENT_ID;
    if (!tracksUsers) return;
    if (getConsentStatus() !== null) return;
    // Defer so it doesn't jank first paint
    const id = setTimeout(() => setVisible(true), 1500);
    return () => clearTimeout(id);
  }, [location.pathname]);

  if (KIOSK_PATHS.includes(location.pathname)) return null;
  if (!visible) return null;

  const accept = () => {
    writeConsent(CONSENT_GRANTED);
    setVisible(false);
    // Let Analytics.js know it can start reporting
    window.dispatchEvent(new CustomEvent('tp:consent-changed', { detail: CONSENT_GRANTED }));
  };

  const reject = () => {
    writeConsent(CONSENT_DENIED);
    setVisible(false);
    window.dispatchEvent(new CustomEvent('tp:consent-changed', { detail: CONSENT_DENIED }));
  };

  return (
    <div
      className="fixed bottom-3 left-3 right-3 md:left-auto md:right-5 md:bottom-5 md:max-w-md z-[100] bg-zinc-950 text-zinc-100 border border-zinc-800 rounded-xl shadow-2xl p-4"
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="text-[11px] uppercase tracking-widest text-emerald-400 mb-1">Heads-up</div>
      <p className="text-sm leading-relaxed text-zinc-200">
        We use a single analytics cookie (Google Analytics) to see which pages get love. No ad
        networks, no third-party trackers.{' '}
        <Link to="/privacy" className="underline text-zinc-300" data-testid="cookie-consent-privacy-link">
          Privacy policy
        </Link>.
      </p>
      <div className="flex gap-2 mt-3">
        <button
          onClick={accept}
          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-md px-3 py-2 text-sm font-semibold"
          data-testid="cookie-consent-accept-btn"
        >
          Got it
        </button>
        <button
          onClick={reject}
          className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-md px-3 py-2 text-sm font-semibold"
          data-testid="cookie-consent-reject-btn"
        >
          Reject
        </button>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
